/**
 * Terms of Service — static public page, no auth required.
 */
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "April 13, 2026";

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Zap size={24} className="text-brand" />
          <span className="text-[20px] font-bold text-brand" style={{ letterSpacing: "-0.02em" }}>enereg</span>
        </Link>

        <article className="bg-surface border border-border rounded-2xl p-6 sm:p-10">
          <h1 className="text-[28px] font-bold text-text-primary mb-1" style={{ letterSpacing: "-0.02em" }}>Terms of Service</h1>
          <p className="text-[14px] text-text-tertiary mb-8">Effective date: {EFFECTIVE_DATE}</p>

          <div className="space-y-8 text-[15px] leading-relaxed text-text-secondary">
            {/* 1 */}
            <Section title="1. Service Description">
              <p>
                Enereg is an AI-powered platform that helps renewable energy project developers navigate licensing
                and permitting requirements in African jurisdictions including Kenya, South Africa, Nigeria, Ghana,
                Namibia, and Zambia. The platform provides:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Structured tracking of regulatory field data across project lifecycles</li>
                <li>AI-powered document extraction to populate regulatory fields from uploaded documents</li>
                <li>Cloud drive integration (Google Drive, OneDrive) for automatic document syncing</li>
                <li>License dependency tracking and compliance monitoring</li>
                <li>Readiness assessments, gap analyses, and actionable guidance</li>
                <li>Pre-filled regulatory submission templates</li>
              </ul>
            </Section>

            {/* 2 */}
            <Section title="2. Account and Access">
              <p>
                Enereg is currently in <strong>beta</strong>. Access requires a beta access code provided by the
                Enereg team. You may create one account per email address.
              </p>
              <p className="mt-3">
                You are responsible for maintaining the security of your account. You must not share your beta
                access code or account credentials with unauthorized parties. You must notify us immediately if
                you become aware of any unauthorized use of your account.
              </p>
              <p className="mt-3">
                We reserve the right to suspend or terminate accounts that violate these terms, are inactive for
                an extended period, or are used in ways that compromise platform security.
              </p>
            </Section>

            {/* 3 */}
            <Section title="3. Cloud Drive Integration">
              <p>
                Enereg offers optional integration with Google Drive and Microsoft OneDrive. By connecting a cloud
                drive to your project, you:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>
                  <strong>Grant read-only access</strong> to the specific folder you select. Enereg will not access
                  any other files or folders in your drive.
                </li>
                <li>
                  <strong>Authorize automatic syncing.</strong> Enereg periodically checks the connected folder for
                  new or modified files and processes them for data extraction.
                </li>
                <li>
                  <strong>Can disconnect at any time.</strong> You may remove the cloud drive connection from the
                  Documents tab in your project dashboard. This revokes Enereg's access to your drive.
                </li>
              </ul>
              <p className="mt-3">
                Cloud drive integration is entirely voluntary. The platform functions fully without it — you can
                always upload documents directly instead.
              </p>
            </Section>

            {/* 4 */}
            <Section title="4. AI-Generated Content">
              <p className="font-semibold text-text-primary">
                Enereg is not a law firm, regulatory consultancy, or financial advisor.
              </p>
              <p className="mt-3">
                The platform uses artificial intelligence to extract data from documents, generate readiness
                assessments, identify gaps in your regulatory filings, and produce guidance. While we strive for
                accuracy, AI-generated content may contain errors or omissions.
              </p>
              <p className="mt-3">
                <strong>You are responsible for verifying all AI-extracted data and AI-generated guidance</strong>{" "}
                before relying on it for regulatory submissions, business decisions, or any other purpose. The
                platform includes confidence scores and review workflows specifically to help you identify
                values that need manual verification.
              </p>
              <p className="mt-3">
                Nothing in the Enereg platform constitutes legal advice, regulatory advice, or professional
                consulting. You should consult qualified professionals for decisions related to regulatory
                compliance, licensing applications, and investment readiness.
              </p>
            </Section>

            {/* 5 */}
            <Section title="5. Acceptable Use">
              <p>You agree to use Enereg only for lawful purposes related to renewable energy project management. You must not:</p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Attempt to gain unauthorized access to other users' data or accounts</li>
                <li>Use the platform to store or transmit malicious software</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code of the platform</li>
                <li>Use automated tools (bots, scrapers) to access the platform except through our documented API</li>
                <li>Upload documents containing content that is illegal or violates third-party rights</li>
                <li>Misrepresent your identity or affiliation</li>
              </ul>
            </Section>

            {/* 6 */}
            <Section title="6. Intellectual Property">
              <p>
                <strong>Your data:</strong> You own all project data, documents, and content you upload to Enereg.
                We claim no intellectual property rights over your content. You grant us a limited license to
                process your content solely to provide the service (extraction, analysis, template generation).
              </p>
              <p className="mt-3">
                <strong>Our platform:</strong> Enereg owns the platform software, design, regulatory field
                definitions, seed data, and knowledge base content. You may not copy, distribute, or create
                derivative works from the platform.
              </p>
            </Section>

            {/* 7 */}
            <Section title="7. Service Availability">
              <p>
                Enereg is provided as a beta service. We make reasonable efforts to maintain availability but
                do not guarantee uninterrupted service. We may:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Perform maintenance that temporarily affects availability</li>
                <li>Modify features, functionality, or the user interface</li>
                <li>Discontinue features with reasonable notice</li>
              </ul>
              <p className="mt-3">
                We do not offer a Service Level Agreement (SLA) during the beta period. We will provide
                reasonable notice before any changes that significantly affect your use of the platform.
              </p>
            </Section>

            {/* 8 */}
            <Section title="8. Limitation of Liability">
              <p>
                To the maximum extent permitted by applicable law, Enereg and its officers, directors, employees,
                and agents shall not be liable for any indirect, incidental, special, consequential, or punitive
                damages, including but not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Regulatory penalties, fines, or compliance failures resulting from reliance on AI-generated content</li>
                <li>Delays in licensing or permitting processes</li>
                <li>Inaccuracies in extracted data or generated assessments</li>
              </ul>
              <p className="mt-3">
                Our total liability for any claims arising from your use of the platform shall not exceed the
                amount you paid us in the 12 months preceding the claim, or ZAR 5,000, whichever is greater.
              </p>
            </Section>

            {/* 9 */}
            <Section title="9. Governing Law and Disputes">
              <p>
                These Terms are governed by the laws of the Republic of South Africa. Any disputes arising from
                these Terms or your use of the platform shall be subject to the exclusive jurisdiction of the
                courts of South Africa.
              </p>
              <p className="mt-3">
                Before initiating formal proceedings, both parties agree to attempt to resolve disputes through
                good-faith negotiation for a period of at least 30 days.
              </p>
            </Section>

            {/* 10 */}
            <Section title="10. Changes to These Terms">
              <p>
                We may update these Terms from time to time. We will notify you of significant changes by posting
                a notice on the platform at least 14 days before the changes take effect. Your continued use of
                the platform after changes take effect constitutes acceptance of the updated Terms.
              </p>
              <p className="mt-3">
                If you have questions about these Terms, contact us at:
              </p>
              <p className="mt-3 font-medium text-text-primary">
                Enereg<br />
                Email: legal@enereg.com
              </p>
            </Section>
          </div>
        </article>

        <footer className="mt-8 text-center text-[13px] text-text-tertiary">
          <Link to="/privacy" className="hover:text-text-secondary">Privacy Policy</Link>
          <span className="mx-2">&middot;</span>
          <Link to="/" className="hover:text-text-secondary">Back to enereg</Link>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-[18px] font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </section>
  );
}
